// pages/admin/DashboardAdmin.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Newspaper, GraduationCap, Users, 
  BarChart3, Settings, Shield, BookOpen, TrendingUp,
  FileText, Calendar, CheckCircle, AlertCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const DashboardAdmin = () => {
  const navigate = useNavigate();
  const { userRole, user } = useAuth();
  const [stats, setStats] = useState({
    totalArticles: 0,
    articlesPublies: 0,
    totalFormations: 0,
    totalApprenants: 0,
    totalCompetences: 0,
    articlesRecents: 0
  });
  const [loading, setLoading] = useState(true);

  // Charger les statistiques
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Compter les articles
      const { count: totalArticles } = await supabase
        .from('actualites')
        .select('*', { count: 'exact', head: true });

      const { count: articlesPublies } = await supabase
        .from('actualites')
        .select('*', { count: 'exact', head: true })
        .eq('is_published', true);

      // Articles des 7 derniers jours
      const dateLimit = new Date();
      dateLimit.setDate(dateLimit.getDate() - 7);
      const { count: articlesRecents } = await supabase
        .from('actualites')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', dateLimit.toISOString());

      // Compter les formations
      const { count: totalFormations } = await supabase
        .from('formations')
        .select('*', { count: 'exact', head: true });

      // Compter les compétences
      const { count: totalCompetences } = await supabase
        .from('referentiel_competences')
        .select('*', { count: 'exact', head: true });

      setStats({
        totalArticles: totalArticles || 0,
        articlesPublies: articlesPublies || 0,
        totalFormations: totalFormations || 0,
        totalApprenants: 0,
        totalCompetences: totalCompetences || 0,
        articlesRecents: articlesRecents || 0
      });
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cartes de statistiques
  const statsCards = [
    {
      title: 'Articles Total',
      value: stats.totalArticles,
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-500/10',
      iconColor: 'text-blue-400'
    },
    {
      title: 'Articles Publiés',
      value: stats.articlesPublies,
      icon: CheckCircle,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-500/10',
      iconColor: 'text-green-400'
    },
    {
      title: 'Formations',
      value: stats.totalFormations,
      icon: GraduationCap,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-500/10',
      iconColor: 'text-purple-400'
    },
    {
      title: 'Compétences',
      value: stats.totalCompetences,
      icon: BookOpen,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-500/10',
      iconColor: 'text-orange-400'
    }
  ];

  // Cartes d'action - Accessibles selon le rôle
  const actionCards = [
    {
      title: 'Édition des Articles',
      description: 'Gérer les actualités Auto et Vente',
      icon: Newspaper,
      path: '/admin/articles',
      color: 'from-blue-600 to-cyan-600',
      roles: ['super_admin', 'admin', 'formateur']
    },
    {
      title: 'Gestion des Compétences',
      description: 'Référentiels et sous-compétences',
      icon: BookOpen,
      path: '/admin/competences',
      color: 'from-purple-600 to-pink-600',
      roles: ['super_admin', 'admin']
    },
    {
      title: 'Toutes les Formations',
      description: 'Vue globale des sessions',
      icon: GraduationCap,
      path: '/admin/formations',
      color: 'from-green-600 to-emerald-600',
      roles: ['super_admin', 'admin', 'formateur']
    },
    {
      title: 'Gestion des Apprenants',
      description: 'Suivi des élèves',
      icon: Users,
      path: '/admin/apprenants',
      color: 'from-yellow-600 to-orange-600',
      roles: ['super_admin', 'admin', 'formateur']
    },
    {
      title: 'Statistiques',
      description: 'Tableaux de bord et rapports',
      icon: BarChart3,
      path: '/admin/statistiques',
      color: 'from-red-600 to-rose-600',
      roles: ['super_admin', 'admin']
    },
    {
      title: 'Gestion des Rôles',
      description: 'Attribution des permissions',
      icon: Shield,
      path: '/admin/roles',
      color: 'from-indigo-600 to-purple-600',
      roles: ['super_admin']
    },
    {
      title: 'Paramètres',
      description: 'Configuration du système',
      icon: Settings,
      path: '/admin/parametres',
      color: 'from-gray-600 to-slate-600',
      roles: ['super_admin', 'admin']
    }
  ];

  // Filtrer les cartes selon le rôle
  const accessibleCards = actionCards.filter(card => 
    card.roles.includes(userRole)
  );

  return (
    <div className="px-8 pb-8">
      <div className="max-w-7xl mx-auto">
        {/* Cartes de statistiques */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          {statsCards.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                </div>
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">{stat.title}</p>
                <p className="text-3xl font-bold text-white">
                  {loading ? '...' : stat.value}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Cartes d'action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
            Accès Rapide
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {accessibleCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ scale: 1.03, y: -5 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(card.path)}
                className="group relative bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-slate-600 cursor-pointer overflow-hidden transition-all"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-lg bg-gradient-to-br ${card.color} shadow-lg`}>
                      <card.icon className="w-6 h-6 text-white" />
                    </div>
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </motion.div>
                  </div>

                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 transition-all">
                    {card.title}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {card.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Activité récente */}
        {stats.articlesRecents > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12 bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-slate-700"
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-5 h-5 text-blue-400" />
              <h3 className="text-lg font-semibold text-white">
                Activité Récente
              </h3>
            </div>
            <p className="text-gray-300">
              <span className="text-blue-400 font-semibold">{stats.articlesRecents}</span> article(s) créé(s) cette semaine
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default DashboardAdmin;